import { useTranslation } from "react-i18next";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { DEFAULT_PAGE_SIZE } from "~/hooks/search/constants";
import {
  SearchProductWithScore,
  type SearchQuery,
  SearchType,
} from "~/hooks/search/types";
import { columns } from "~/pages/books/table/columns";
import { useTable } from "~/hooks/use-table";
import { Text } from "~/components/text";
import { BooksPageTable } from "./books-page-table";
import { BooksPageHeader } from "~/pages/books/components/books-page-header";

export const RegularBooksTable = ({
  rowSelection,
  setRowSelection,
  isLoading,
  searchQuery,
  setSearchQuery,
  error,
  products,
  count,
  total,
  fetchAllResults,
  ageRangeAvailableBounds,
}: {
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  isLoading: boolean;
  searchQuery: SearchQuery;
  setSearchQuery: (query: Partial<SearchQuery>) => void;
  error: Error | null | undefined;
  products: SearchProductWithScore[];
  count: number;
  total: number | null;
  fetchAllResults: () => Promise<SearchProductWithScore[]>;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
}) => {
  const { t } = useTranslation(["books"]);
  const navigate = useNavigate();
  const location = useLocation();

  const tableColumns = useMemo(
    () =>
      columns(t, {
        loading: isLoading,
        showMatch:
          searchQuery.type === SearchType.SEMANTIC && !!searchQuery.query,
      }),
    [t, isLoading, searchQuery.type, searchQuery.query],
  );

  const { table } = useTable({
    columns: tableColumns,
    data: products,
    enableRowSelection: true,
    rowSelection,
    setRowSelection,
    totalRecords: count,
    getRowId: (row) => row.id,
  });

  const isSelectedAll = table.getSelectedAll();
  const selectedProducts = isSelectedAll ? "ALL" : table.getSelectedIds();

  const handleBookClick = useCallback(
    (product: SearchProductWithScore) => {
      navigate({
        to: "/books/$productId",
        params: { productId: product.id },
        state: { from: location.href },
      });
    },
    [navigate, location],
  );

  const hasQuery = (searchQuery.query?.trim() ?? "").length > 0;
  const showEndOfList =
    searchQuery.type === SearchType.SEMANTIC &&
    hasQuery &&
    count > 0 &&
    !isLoading;

  return (
    <>
      <BooksPageHeader
        count={count}
        total={total}
        isLoading={isLoading}
        selectedProducts={selectedProducts}
        query={searchQuery.query}
        searchType={searchQuery.type}
      />
      <BooksPageTable
        table={table}
        isLoading={isLoading}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        error={error}
        products={products}
        fetchAllResults={fetchAllResults}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        hasPagination={searchQuery.type !== SearchType.SEMANTIC}
        onBookClick={handleBookClick}
        ageRangeAvailableBounds={ageRangeAvailableBounds}
      />
      {showEndOfList && (
        <Text
          data-testid="semantic-end-of-list"
          variant="muted"
          className="text-center py-4"
        >
          {t("books:search.endOfList")}
        </Text>
      )}
    </>
  );
};
